/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Home (redesign artboard 01). Account-wide overview: every service the user
// can reach, where this month's money went, the wallet, and recent activity.

import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BuilderCreditsBanner } from '../components/home/BuilderCredits';
import { walletService, builderCreditsService } from '../services/api';
import { isManagedMLOpsEnabled, isSandboxEnabled, isVMManagerEnabled } from '../config/featureFlags';
import { TRAINING_PORTAL_URL, INFERENCE_PORTAL_URL } from '../config/consoleNav';
import { useAuthStore } from '../store/authStore';
import type { Transaction } from '../types';
import { Page, LinkButton, Card, Tag, SectionHeader, Skeleton, formatINR } from '../components/console/ui';
import {
  ChipIc, TrendIc, BoltIc, TerminalIc, LayersIc, DriveIc, DbIc, CompassIc, PlusIc,
} from '../components/console/icons';
import ServiceTile, { ServiceTileProps } from '../components/home/ServiceTile';
import { useAccountOverview, formatCompact, formatGb } from '../components/home/useAccountOverview';
import { useUsageByProduct } from '../components/billing/useBilling';
import { USAGE_LABELS } from '../components/billing/billingUtils';

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const formatWhen = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  if (d.toDateString() === now.toDateString()) return `Today ${time}`;
  return `${d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} ${time}`;
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const ov = useAccountOverview();
  const { wallet, currency, expenditure } = ov;
  const [workload, setWorkload] = useState('');

  const { data: paymentMethodsData } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: () => walletService.getPaymentMethods().then((res) => res.data),
  });
  const savedCards: unknown[] = Array.isArray(paymentMethodsData?.data)
    ? paymentMethodsData.data
    : Array.isArray(paymentMethodsData)
      ? paymentMethodsData
      : [];

  const { data: txData, isLoading: txLoading } = useQuery({
    queryKey: ['transactions', 'home'],
    queryFn: () => walletService.getTransactions(1, 5).then((res) => res.data),
  });
  const transactions: Transaction[] = (txData?.transactions || []).slice(0, 5);

  // Welcome header - first word of the user's name, falling back to the email prefix
  const firstName = (() => {
    const nameFirstWord = user?.name?.trim().split(/\s+/)[0];
    if (nameFirstWord) return nameFirstWord;
    if (user?.email) return user.email.split('@')[0];
    return 'there';
  })();

  // Month-to-date spend by product from /wallet/usage-by-product (covers every
  // product, incl. inference/TrainPods). Older backends: /wallet/expenditure,
  // whose per-product numbers are last-7-days only.
  const { data: usage, isLoading: usageLoading } = useUsageByProduct();
  const monthSpend = usage ? usage.total : expenditure?.monthly_expenditure ?? 0;

  // Spend breakdown - only categories the backend reports, VMs only when offered.
  const spendRows = useMemo(() => {
    if (usage) {
      const names: Partial<Record<string, string>> = {
        nfs: 'NFS volumes',
        pods: 'GPU Pods',
        vms: 'Virtual machines',
        object_storage: 'Object storage',
      };
      const base = ['nfs', 'pods', ...(isVMManagerEnabled() ? ['vms'] : []), 'object_storage'];
      const valueOf = (k: string) => usage.products.find((p) => p.product === k)?.total ?? 0;
      return [
        ...base.map((k) => ({ name: names[k] as string, value: valueOf(k) })),
        ...usage.products
          .filter((p) => !base.includes(p.product) && p.total > 0)
          .map((p) => ({ name: USAGE_LABELS[p.product] || p.label, value: p.total })),
      ];
    }
    return [
      { name: 'NFS volumes', value: expenditure?.storage_expenditure ?? 0 },
      { name: 'GPU Pods', value: expenditure?.container_expenditure ?? 0 },
      ...(isVMManagerEnabled() ? [{ name: 'Virtual machines', value: expenditure?.vm_expenditure ?? 0 }] : []),
      { name: 'Object storage', value: expenditure?.bucket_expenditure ?? 0 },
    ];
  }, [usage, expenditure]);
  const spendBase = Math.max(monthSpend, spendRows.reduce((s, r) => s + r.value, 0));
  const topSpend = [...spendRows].sort((a, b) => b.value - a.value)[0];
  const topShare = spendBase > 0 && topSpend ? Math.round((topSpend.value / spendBase) * 100) : 0;
  const today = new Date();
  const monthRange = `1–${today.getDate()} ${today.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;

  const pricedFrom = ov.gpus.cheapest ? ` · from ${formatINR(ov.gpus.cheapest.perHour, currency, 0)}/hr` : '';

  const tiles: Array<ServiceTileProps & { id: string }> = [
    {
      id: 'pods',
      to: '/pods',
      icon: <ChipIc />,
      title: 'QuickPods · GPU Pods',
      sub: `${ov.runningPods} running${pricedFrom}`,
      tag:
        ov.runningPods > 0
          ? { tone: 'ok', label: `${ov.runningPods} running` }
          : ov.gpus.freeTypes.length > 0
            ? { tone: 'ok', label: `${ov.gpus.freeTypes[0]} free` }
            : { tone: 'off', label: 'Idle' },
    },
    {
      id: 'trainpods',
      to: `${TRAINING_PORTAL_URL}/console/gpus`,
      external: true,
      icon: <TrendIc />,
      title: 'TrainPods',
      sub: 'Hourly GPUs · SSH & CLI',
      tag: { tone: 'info', label: 'Training studio' },
    },
    {
      id: 'inference',
      to: `${INFERENCE_PORTAL_URL}/console/models`,
      external: true,
      icon: <BoltIc />,
      title: 'Inference',
      sub: 'Serverless models · per-token pricing',
      tag: { tone: 'info', label: 'OpenAI-compatible' },
    },
    ...(isSandboxEnabled()
      ? [
          {
            id: 'sandboxes',
            to: '/sandboxes',
            icon: <TerminalIc />,
            title: 'Sandboxes',
            sub: `${ov.runningSandboxes} running · ${Math.max(ov.sandboxes.length - ov.runningSandboxes, 0)} past`,
            tag:
              ov.runningSandboxes > 0
                ? { tone: 'ok' as const, label: `${ov.runningSandboxes} running` }
                : { tone: 'off' as const, label: 'Idle' },
          },
        ]
      : []),
    ...(isManagedMLOpsEnabled()
      ? [
          {
            id: 'mlops',
            to: '/managed-mlops',
            icon: <LayersIc />,
            title: 'Managed MLOps',
            sub: 'MLflow, Kubeflow, DVC, Metaflow',
            tag:
              ov.activeManaged > 0
                ? { tone: 'ok' as const, label: `${ov.activeManaged} active` }
                : { tone: 'off' as const, label: 'None active' },
          },
        ]
      : []),
    {
      id: 'volumes',
      to: '/volumes',
      icon: <DriveIc />,
      title: 'QuickPods · NFS volumes',
      sub: ov.volumes.length > 0 ? `${formatGb(ov.volumeGb)} provisioned` : 'Persistent storage for pods',
      tag: ov.volumes.length > 0 ? { tone: 'ok', label: `${ov.volumes.length} active` } : { tone: 'off', label: 'None' },
    },
    {
      id: 'objects',
      to: '/drive',
      icon: <DbIc />,
      title: 'Object storage',
      sub: `${plural(ov.buckets.length, 'bucket')} · S3-compatible`,
      tag: ov.buckets.length > 0 ? { tone: 'ok', label: `${ov.buckets.length} active` } : { tone: 'off', label: 'Empty' },
    },
    {
      id: 'advisor',
      to: '/advisor',
      icon: <CompassIc />,
      title: 'Compute Advisor',
      sub: 'GPU picks from live pricing',
      tag: { tone: 'info', label: 'Free' },
    },
  ];

  const submitAdvisor = (e: React.FormEvent) => {
    e.preventDefault();
    const q = workload.trim();
    navigate(q ? `/advisor?q=${encodeURIComponent(q)}` : '/advisor');
  };

  const isPostpaid = wallet?.account_type === 'postpaid';
  const autoPay = !!wallet?.auto_debit_enabled;
  const walletNotice = wallet?.services_stopped_for_balance
    ? { tone: 'err', text: 'Some services were stopped because the balance ran out. Add funds to restart them.' }
    : wallet?.is_low_balance
      ? {
          tone: 'warn',
          text:
            wallet.estimated_hours_left > 0
              ? `Balance is low - about ${Math.floor(wallet.estimated_hours_left)} h left at the current run rate.`
              : 'Balance is low for your running resources.',
        }
      : autoPay && savedCards.length === 0
        ? { tone: 'warn', text: 'Auto-pay is on but no card is saved. Add one so pods aren’t stopped when the balance runs out.' }
        : null;

  return (
    <Page>
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div className="min-w-0">
          <h1 className="m-0 text-2xl sm:text-[28px] font-medium text-ps-text">
            {greeting()}, {firstName}
          </h1>
          <p className="mt-1.5 mb-0 text-ps-muted text-[15px]">
            {ov.loading ? (
              <Skeleton className="h-4 w-64 inline-block align-middle" />
            ) : (
              <>
                {plural(ov.runningPods, 'pod')} running · {plural(ov.volumes.length, 'volume')} ·{' '}
                {formatCompact(monthSpend, currency)} spent this month
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2.5 max-[820px]:w-full max-[820px]:[&>*]:flex-1 max-[820px]:[&>*]:min-h-[44px]">
          <LinkButton to="/services" variant="secondary">
            Browse all services
          </LinkButton>
          <LinkButton to="/pods/create">
            <PlusIc className="w-4 h-4" />
            Launch GPU pod
          </LinkButton>
        </div>
      </div>

      <BuilderCreditsBanner
        onApply={async (data) => {
          await builderCreditsService.apply(data);
        }}
        kycEnabled={process.env.REACT_APP_ENABLE_BUILDER_CREDITS_KYC === 'true'}
      />

      {/* Compute Advisor callout */}
      <Card as="section" aria-label="Compute Advisor" className="p-[22px] flex flex-wrap gap-5 items-center !bg-[#140C06] !border-[#5B2A0E]">
        <div className="flex-[1_1_320px] flex flex-col gap-1.5">
          <div className="ps-eyebrow">Compute Advisor</div>
          <div className="text-lg font-semibold">Not sure which service or GPU fits? Describe the job.</div>
        </div>
        <form onSubmit={submitAdvisor} className="flex-[2_1_420px] flex flex-wrap gap-2.5">
          <label className="sr-only" htmlFor="home-advisor">
            Describe your workload
          </label>
          <input
            id="home-advisor"
            value={workload}
            onChange={(e) => setWorkload(e.target.value)}
            maxLength={500}
            placeholder="e.g. Fine-tune a 13B model on internal docs under ₹5,000"
            className="ps-input flex-[1_1_260px] !w-auto min-h-[44px] !border-[#5B2A0E]"
          />
          <button type="submit" className="ps-btn min-h-[44px] max-[820px]:w-full">
            Recommend
          </button>
        </form>
      </Card>

      {/* Your services */}
      <section className="flex flex-col gap-3.5" aria-labelledby="home-svc">
        <SectionHeader
          id="home-svc"
          title="Your services"
          action={
            <Link to="/services" className="ps-link">
              See all services →
            </Link>
          }
        />
        <div className="grid gap-3.5 grid-cols-[repeat(auto-fill,minmax(min(100%,220px),1fr))]">
          {tiles.map(({ id, ...t }) => (
            <ServiceTile key={id} {...t} />
          ))}
        </div>
      </section>

      <div className="flex flex-wrap gap-5">
        {/* Spend this month */}
        <Card as="section" aria-labelledby="home-spend" className="flex-[2_1_460px] min-w-0 p-[22px] flex flex-col gap-[18px]">
          <SectionHeader
            id="home-spend"
            title="Spend this month"
            action={
              <Link to="/billing" className="ps-link">
                Usage details →
              </Link>
            }
          />
          {usageLoading || (!usage && ov.expenditureLoading) ? (
            <Skeleton className="h-28 w-full" />
          ) : (
            <>
              <div className="flex flex-wrap gap-7">
                <div>
                  <div className="text-[32px] font-semibold leading-tight">{formatINR(monthSpend, currency, 0)}</div>
                  <div className="text-ps-muted text-[13px]">{monthRange}</div>
                </div>
                <div className="min-w-0">
                  {spendBase > 0 && topSpend ? (
                    <>
                      <div className="text-[15px] font-medium pt-2">
                        {topSpend.name}{' '}
                        {topSpend.name === 'Object storage'
                          ? topShare >= 100 ? 'is' : 'accounts for'
                          : topShare >= 100 ? 'are' : 'account for'}{' '}
                        {topShare}% of spend
                      </div>
                      {topSpend.name === 'NFS volumes' && ov.volumes.length > 0 && (
                        <div className="text-ps-muted text-[13px]">
                          {formatGb(ov.volumeGb)} billed monthly{ov.runningPods === 0 ? ', even with no pods running' : ''}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-[15px] font-medium pt-2 text-ps-muted">No spend recorded yet this month</div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-3">
                {spendRows.map((r) => {
                  const pct = spendBase > 0 ? (r.value / spendBase) * 100 : 0;
                  return (
                    <div
                      key={r.name}
                      className={`grid grid-cols-[110px_1fr_80px] sm:grid-cols-[140px_1fr_90px] gap-3 items-center text-sm ${
                        r.value > 0 ? '' : 'text-ps-muted'
                      }`}
                    >
                      <span className="truncate">{r.name}</span>
                      <div className="h-2.5 bg-ps-raised rounded-full" role="presentation">
                        {pct > 0 && (
                          <div className="h-2.5 bg-ps-accent rounded-full" style={{ width: `${Math.max(pct, 1)}%` }} />
                        )}
                      </div>
                      <span className="text-right">{formatCompact(r.value, currency)}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Card>

        {/* Wallet */}
        <Card as="section" aria-labelledby="home-wallet" className="flex-[1_1_300px] min-w-0 p-[22px] flex flex-col gap-3.5">
          <h2 id="home-wallet" className="m-0 text-lg font-medium">
            Wallet
          </h2>
          {ov.walletLoading ? (
            <Skeleton className="h-10 w-48" />
          ) : (
            <div className="text-[32px] font-semibold leading-tight">{formatINR(wallet?.balance ?? 0, currency)}</div>
          )}
          {wallet && (
            <div className="flex flex-wrap gap-2">
              <Tag tone="info">{isPostpaid ? 'Postpaid' : 'Prepaid'}</Tag>
              <Tag tone={autoPay ? 'ok' : 'off'}>{autoPay ? 'Auto-pay on' : 'Auto-pay off'}</Tag>
              {wallet.has_running_resources && wallet.hourly_run_rate > 0 && (
                <Tag tone="off">{formatINR(wallet.hourly_run_rate, currency)}/hr</Tag>
              )}
            </div>
          )}
          {walletNotice && (
            <div
              className={`p-3 rounded-md text-[13px] leading-normal ${
                walletNotice.tone === 'err' ? 'bg-ps-err-bg text-ps-err' : 'bg-ps-warn-bg text-ps-warn'
              }`}
            >
              {walletNotice.text}
            </div>
          )}
          <div className="flex flex-wrap gap-2.5 mt-auto max-[820px]:[&>*]:flex-1 max-[820px]:[&>*]:min-h-[44px]">
            <LinkButton to="/billing/payments?action=topup">Add funds</LinkButton>
            {savedCards.length === 0 ? (
              <LinkButton to="/billing/payments" variant="secondary">
                Add card
              </LinkButton>
            ) : (
              <LinkButton to="/billing" variant="secondary">
                Billing
              </LinkButton>
            )}
          </div>
        </Card>
      </div>

      {/* Recent activity */}
      <Card as="section" aria-labelledby="home-activity" className="p-[22px] flex flex-col gap-3">
        <SectionHeader
          id="home-activity"
          title="Recent activity"
          action={
            <Link to="/billing/transactions" className="ps-link">
              All transactions →
            </Link>
          }
        />
        {txLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : transactions.length === 0 ? (
          <p className="m-0 text-sm text-ps-muted py-2">No activity yet. Launch a pod or call a model and it will show up here.</p>
        ) : (
          <ul className="list-none m-0 p-0">
            {transactions.map((t, i) => (
              <li
                key={t.id}
                className={`flex flex-wrap justify-between gap-3 py-2.5 text-sm ${
                  i < transactions.length - 1 ? 'border-b border-ps-raised' : ''
                }`}
              >
                <span className="min-w-0 break-words">{t.description || (t.type === 'credit' ? 'Wallet credit' : 'Charge')}</span>
                <span className="text-ps-muted whitespace-nowrap">
                  {formatWhen(t.created_at)} · {t.type === 'credit' ? '+' : ''}
                  {formatINR(t.amount, t.currency || currency)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Page>
  );
};

export default Dashboard;
